import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import React, { useCallback, useRef } from "react";
import HighChartsExport from "highcharts/modules/exporting";
import HighChartsMore from "highcharts/highcharts-more";
import HighChartsSeriesLabels from "highcharts/modules/series-label";
import HighchartsAccessibility from "highcharts/modules/accessibility";
import { RawChart } from "interfaces/chart";
import { useMappedChartOptions } from "core/chart-mappers/aihub-chart-to-highcharts";
import { styled } from "@mui/material/styles";
import { useResizeDetector } from "react-resize-detector";
import ProgressIndicator from "components/ProgressIndicator";

HighChartsExport(Highcharts);
HighChartsMore(Highcharts);
HighChartsSeriesLabels(Highcharts);
HighchartsAccessibility(Highcharts);

export interface RawDataSourceProps {
  chart: RawChart;
  loading?: boolean;
}

export const RawDataSourceChart = ({
  chart,
  loading = false
}: RawDataSourceProps): JSX.Element => {
  const ref = useRef<HighchartsReact.RefObject>(null);

  // Need to manually tell the graph to resize, as it breaks flexbox otherwise.
  const onResize = useCallback(() => {
    ref.current?.chart.reflow();
  }, []);

  const { ref: wrapperRef } = useResizeDetector({ onResize });

  const chartOptions = useMappedChartOptions(chart);

  return (
    <Wrapper ref={wrapperRef}>
      <ChartWrapper>
        <HighchartsReact
          id="container"
          highcharts={Highcharts}
          options={chartOptions}
          ref={ref}
          immutable // FIXME: remove this when figured out how to fix tests
        />
      </ChartWrapper>
      <LoadingWrapper>
        <ProgressIndicator loading={loading} error={false} delay={500} />
      </LoadingWrapper>
    </Wrapper>
  );
};

const Wrapper = styled("div")`
  flex-grow: 1;
  overflow: hidden;
  position: relative;
`;

const ChartWrapper = styled("div")`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const LoadingWrapper = styled("div")`
  position: absolute;
  bottom: 50px;
  right: 50px;
  z-index: 1;
`;
